import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import 'moment-timezone';
import { BehaviorSubject, Observable } from 'rxjs';

import { DEFAULT_MENU_OPTIONS, IMenuOptions } from '../models/menu-options';

/** A service to update the mouse mode */
@Injectable({ providedIn: 'root' })
export class MenuOptionsService {
    menuOptionsMap: Map<string, BehaviorSubject<IMenuOptions>> = new Map();
    globalMenuOptions: IMenuOptions = DEFAULT_MENU_OPTIONS;

    constructor() {
        this.globalMenuOptions.useGlobalSettings = true;
    }

    /** add a default menu options config to our map for a plot. Allow a default config to be specified as an optional parameter */
    initMenuOptions( plotId: string, menuOptions?: IMenuOptions ): void {
        if ( menuOptions == null ) {
            menuOptions = cloneDeep( this.globalMenuOptions );
        }
        this.menuOptionsMap.set( plotId, new BehaviorSubject( menuOptions ));
    }

    /** set the menu options for a plot */
    setMenuOptions( plotId: string, options: IMenuOptions ): void {
        this.menuOptionsMap.get( plotId ).next( options );
    }

    /** enable or disable global options for the given plot */
    toggleGlobalOptions( plotId: string ) {
        const currentMenuOptions = this.getMenuOptions( plotId );
        currentMenuOptions.useGlobalSettings = !currentMenuOptions.useGlobalSettings;
        if ( currentMenuOptions.useGlobalSettings ) {
            this.setMenuOptions( plotId, this.globalMenuOptions );
        }
    }

    /** turn off global options for all plots */
    disableGlobalOptions() {
        this.globalMenuOptions.useGlobalSettings = false;
        this.setGlobalMenuOptions( this.globalMenuOptions );
        const plotIds = Array.from( this.menuOptionsMap.keys() );
        plotIds.forEach( plotId => {
            const menuOptions = this.getMenuOptions( plotId );
            menuOptions.useGlobalSettings = false;
            this.setMenuOptions( plotId, menuOptions );
        });
    }

    /** get the current menu options for a plot */
    getMenuOptions( plotId: string ): IMenuOptions {
        return this.menuOptionsMap.get( plotId ).getValue();
    }

    /** returns an observable that emits when we updat the menu config for a plot */
    getMenuOptions$( plotId: string ): Observable<IMenuOptions> {
        return this.menuOptionsMap.get( plotId );
    }

    /** set the global menu options to a new set of options. Find any plots with global options enabled and upated them */
    setGlobalMenuOptions( menuOptions: IMenuOptions ): void {
        this.globalMenuOptions = menuOptions;
        for ( const plotMenuOptions$ of this.menuOptionsMap.values() ) {
            if ( plotMenuOptions$.getValue().useGlobalSettings ) {
                plotMenuOptions$.next( cloneDeep( this.globalMenuOptions ) );
            }
        }
    }

    /** return the current global options config */
    getGlobalMenuOptions(): IMenuOptions {
        return this.globalMenuOptions;
    }
}
